import { useState } from "react";
import { t, Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import { useRouter } from "next/router";
import { FormikProps, useFormik } from "formik";
import { signIn } from "src/utils/nextAuth";
import prepareNextAuthErrorMessage from "src/utils/prepareNextAuthErrorMessage";
import { isValidEmail } from "src/utils/validations";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import Input from "src/atoms/Input";
import { Button } from "src/atoms/Button";
import { PasswordInput } from "src/atoms/PasswordInput";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { Form } from "./LoginForm.styled";
import { useLingui } from "@lingui/react";
import Link from "next/link";
import { GATED_POPUP_SOURCE } from "src/constants/gatedItem";
import { SignLocation } from "src/tracking/segment";

type FormikValues = {
  email: string;
  password: string;
  grant_type: string;
};

type FormikErrors = {
  email?: string;
  password?: string;
};

type LoginFormProps = {
  fullWidth?: boolean;
  origin?: string;
  isCheckoutPath?: boolean;
  onContinueAsGuest?: () => void;
};

function LoginForm({
  fullWidth = false,
  origin,
  isCheckoutPath,
  onContinueAsGuest,
}: LoginFormProps): JSX.Element {
  const [error, setError] = useState<string | undefined>(undefined);

  const router = useRouter();
  const { _, i18n } = useLingui();

  const formik: FormikProps<FormikValues> = useFormik<FormikValues>({
    initialValues: {
      email: "",
      password: "",
      grant_type: "personal",
    },
    validate: (values) => {
      const errors: FormikErrors = {};
      if (!values.email) {
        errors.email = _(FORM_ERROR_MESSAGES.required);
      } else if (!isValidEmail(values.email)) {
        errors.email = _(FORM_ERROR_MESSAGES.invalidEmail);
      }
      if (!values.password) {
        errors.password = _(FORM_ERROR_MESSAGES.required);
      }
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setError(undefined);

      const response = await signIn(AuthProvider.CREDENTIALS, {
        redirect: false,
        ...values,
        callbackUrl: router.query.redirect_to
          ? (router.query.redirect_to as string)
          : undefined,
        ...(isCheckoutPath ? { location: SignLocation.CHECKOUT } : {}),
      });

      if (response?.error) {
        setError(prepareNextAuthErrorMessage(response.error, i18n));
        return;
      }

      if (response?.url) {
        const url = new URL(response.url);
        url.searchParams.delete("signin");

        if (origin) {
          url.searchParams.append("source", GATED_POPUP_SOURCE);
        }

        void router.push(url.toString());
      }
    },
  });

  return (
    <Form
      className={fullWidth ? "full-width" : ""}
      noValidate
      onSubmit={formik.handleSubmit}
    >
      <Input
        inputTestId="login-form-email-input"
        type="text"
        placeholder={t(i18n)`Email address`}
        {...formik.getFieldProps("email")}
      />

      {formik.errors.email && formik.touched.email ? (
        <Text
          as="p"
          mt={-2}
          color="semantic.red"
          textAlign="left"
          fontSize={0}
          lineHeight="1.43"
        >
          <span role="alert" id="email-error-alert">
            {formik.errors.email}
          </span>
        </Text>
      ) : null}

      <PasswordInput
        data-testid="login-form-password-input"
        placeholder={t(i18n)`Password`}
        {...formik.getFieldProps("password")}
      />

      {formik.errors.password && formik.touched.password ? (
        <Text
          as="p"
          mt={-2}
          color="semantic.red"
          textAlign="left"
          fontSize={0}
          lineHeight="1.43"
        >
          <span role="alert" id="password-error-alert">
            {formik.errors.password}
          </span>
        </Text>
      ) : null}

      {error ? (
        <Text color="semantic.red" data-testid="login-form-error-message">
          {error}
        </Text>
      ) : null}

      {isCheckoutPath ? (
        <Link href={"/checkout"} onClick={onContinueAsGuest} prefetch={false}>
          <Button type="button" variant="secondary" fullWidth>
            <Trans>Continue as a guest</Trans>
          </Button>
        </Link>
      ) : null}

      <Button
        data-testid="login-form-login-button"
        fullWidth
        type="submit"
        variant="primary"
        disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
      >
        <Trans>Log in</Trans>
      </Button>
    </Form>
  );
}

export default LoginForm;
